import { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
// @mui
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Unstable_Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import Iconify from 'src/components/iconify';
import { paths } from 'src/routes/paths';
// redux
import { useDispatch } from 'src/redux/store';
import { getCart } from 'src/redux/slices/checkout';
// _mock
import { PRODUCT_CHECKOUT_STEPS } from 'src/_mock/_product';
// components
import { useSettingsContext } from 'src/components/settings';
//
import UserNewEditForm from 'src/sections/user/user-new-edit-form';
import { useCheckout } from '../../hooks';
import CheckoutCart from '../checkout-cart';
import CheckoutSteps from '../checkout-steps';
import CheckoutPayment from '../checkout-payment';
import CheckoutOrderComplete from '../checkout-order-complete';
import CheckoutBillingAddress from '../checkout-billing-address';
import CheckoutSummary from '../checkout-summary';

// ----------------------------------------------------------------------

function useInitial(cart) {
  const dispatch = useDispatch();

  const getCartCallback = useCallback(() => {
    if (cart.length) {
      dispatch(getCart(cart));
    }
  }, [cart, dispatch]);

  useEffect(() => {
    getCartCallback();
  }, [getCartCallback]);

  return null;
}

// const activeStep = 3;
export default function CheckoutView({ layoutVariant = 'default' }) {
  const settings = useSettingsContext();
  const isOrderingV2 = layoutVariant === 'orderingV2';
  const [searchParams] = useSearchParams();
  const menuQuery = searchParams.toString();
  const menuReturnTo = menuQuery
    ? `${paths.dashboard.v2.product.root}?${menuQuery}`
    : paths.dashboard.v2.product.root;

  let containerMaxWidth = 'lg';
  if (settings.themeStretch) {
    containerMaxWidth = false;
  } else if (isOrderingV2) {
    containerMaxWidth = 'lg';
  }

  const {
    checkout,
    completed,
    onResetAll,
    onGotoStep,
    onNextStep,
    onBackStep,
    onDeleteCart,
    onResetBilling,
    onCreateBilling,
    onApplyDiscount,
    onApplyShipping,
    onIncreaseQuantity,
    onDecreaseQuantity,
  } = useCheckout();

  const { cart, billing, activeStep } = checkout;

  useInitial(cart);

  useEffect(() => {
    if (activeStep === 1) {
      onResetBilling();
    }
  }, [activeStep,onResetBilling]);

  let checkoutMain = null;
  if (completed) {
    checkoutMain = <CheckoutOrderComplete open={completed} onReset={onResetAll} onDownloadPDF={() => {}} />;
  } else if (isOrderingV2 && activeStep === 0) {
    checkoutMain = (
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid xs={12} md={8}>
          <CheckoutCart
            layoutVariant="orderingV2ListOnly"
            checkout={checkout}
            onNextStep={onNextStep}
            onDeleteCart={onDeleteCart}
            onApplyDiscount={onApplyDiscount}
            onIncreaseQuantity={onIncreaseQuantity}
            onDecreaseQuantity={onDecreaseQuantity}
          />
        </Grid>
        <Grid xs={12} md={4}>
          <CheckoutSummary
            enableDiscount
            total={checkout.total}
            discount={checkout.discount}
            subTotal={checkout.subTotal}
            onApplyDiscount={onApplyDiscount}
          />
          <Button
            fullWidth
            size="large"
            variant="contained"
            sx={{ mt: 2, bgcolor: '#0e7c62', '&:hover': { bgcolor: '#065f4a' } }}
            disabled={!checkout.cart.length}
            onClick={onNextStep}
          >
            Check Out
          </Button>
        </Grid>
      </Grid>
    );
  } else {
    checkoutMain = (
      <>
        {activeStep === 0 && (
          <CheckoutCart
            checkout={checkout}
            onNextStep={onNextStep}
            onDeleteCart={onDeleteCart}
            onApplyDiscount={onApplyDiscount}
            onIncreaseQuantity={onIncreaseQuantity}
            onDecreaseQuantity={onDecreaseQuantity}
          />
        )}

        {activeStep === 1 && <UserNewEditForm onNextStep={onNextStep} onBackStep={onBackStep} />}

        {activeStep === 2 && (
          <CheckoutBillingAddress checkout={checkout} onBackStep={onBackStep} onCreateBilling={onCreateBilling} />
        )}

        {activeStep === 3 && billing && (
          <CheckoutPayment
            checkout={checkout}
            onNextStep={onNextStep}
            onBackStep={onBackStep}
            onGotoStep={onGotoStep}
            onApplyShipping={onApplyShipping}
            onReset={onResetAll}
          />
        )}
      </>
    );
  }

  return (
    <Container maxWidth={containerMaxWidth} sx={{ mb: 10 }}>
      <Typography
        variant="h4"
        sx={{
          my: isOrderingV2 ? { xs: 2, md: 3 } : { xs: 3, md: 5 },
          fontWeight: isOrderingV2 ? 800 : 400,
          color: isOrderingV2 ? '#065f4a' : 'text.primary',
        }}
      >
        Checkout
      </Typography>

      {isOrderingV2 && !completed && (
        <Stack direction="row" sx={{ mb: 2 }}>
          <Button
            component={RouterLink}
            to={menuReturnTo}
            color="inherit"
            startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
          >
            Back to menu
          </Button>
        </Stack>
      )}

      <Grid container justifyContent={completed ? 'center' : 'flex-start'}>
        <Grid xs={12} md={isOrderingV2 && !completed && activeStep === 0 ? 12 : 8}>
          <CheckoutSteps activeStep={activeStep} steps={PRODUCT_CHECKOUT_STEPS} />
        </Grid>
      </Grid>

      {checkoutMain}
    </Container>
  );
}

CheckoutView.propTypes = {
  layoutVariant: PropTypes.oneOf(['default', 'orderingV2']),
};
